import React, { createContext, useContext, useMemo, useState, useEffect, ReactNode } from 'react';
import { ThemeProvider, CssBaseline } from '@mui/material';
import type { PaletteMode } from '@mui/material';
import { createAppTheme } from '../theme';

/**
 * Light/dark mode for the admin panel.
 *
 * Defaults to the operating system preference, remembers an explicit choice in
 * localStorage, and keeps following the system while no explicit choice has
 * been made.
 */

type ModePreference = PaletteMode | 'system';

interface ColorModeContextType {
  /** The mode actually being rendered. */
  mode: PaletteMode;
  /** What the user chose, which may be 'system'. */
  preference: ModePreference;
  setPreference: (preference: ModePreference) => void;
  toggleColorMode: () => void;
}

const STORAGE_KEY = 'pickupninja-admin-color-mode';

const ColorModeContext = createContext<ColorModeContextType | undefined>(undefined);

export const useColorMode = () => {
  const context = useContext(ColorModeContext);
  if (context === undefined) {
    throw new Error('useColorMode must be used within a ColorModeProvider');
  }
  return context;
};

function readStoredPreference(): ModePreference {
  // Storage can throw in a private window or when site data is blocked.
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') {
      return stored;
    }
  } catch (_e) {
    /* fall through to the default */
  }
  return 'system';
}

function systemPrefersDark(): boolean {
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  } catch (_e) {
    return false;
  }
}

export const ColorModeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [preference, setPreferenceState] = useState<ModePreference>(readStoredPreference);
  const [systemDark, setSystemDark] = useState<boolean>(systemPrefersDark);

  // Keep following the OS while the preference is 'system'.
  useEffect(() => {
    let media: MediaQueryList;
    try {
      media = window.matchMedia('(prefers-color-scheme: dark)');
    } catch (_e) {
      return;
    }

    const handler = (event: MediaQueryListEvent) => setSystemDark(event.matches);

    // Safari < 14 only supports the deprecated listener API.
    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', handler);
      return () => media.removeEventListener('change', handler);
    }
    media.addListener(handler);
    return () => media.removeListener(handler);
  }, []);

  const setPreference = (next: ModePreference) => {
    setPreferenceState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch (_e) {
      /* a remembered preference is a convenience, not a requirement */
    }
  };

  const mode: PaletteMode =
    preference === 'system' ? (systemDark ? 'dark' : 'light') : preference;

  const theme = useMemo(() => createAppTheme(mode), [mode]);

  const value = useMemo<ColorModeContextType>(
    () => ({
      mode,
      preference,
      setPreference,
      toggleColorMode: () => setPreference(mode === 'dark' ? 'light' : 'dark'),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mode, preference]
  );

  return (
    <ColorModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        {/* enableColorScheme sets the browser's own form/scrollbar colours */}
        <CssBaseline enableColorScheme />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
};
